import React, { useState, useEffect } from 'react';
import { TextField, Button, Container, Box, Typography, Divider } from '@mui/material';
import { createTeam, getTeamById, updateTeam } from '../services/teamService';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MediaUploadField from './MediaUploadField';

function TeamForm() {
  const [initialValues, setInitialValues] = useState({
    name: '',
    nameAr: '',
    role: '',
    roleAr: '',
    bio: '',
    bioAr: '',
    imageSrc: '',
    order: 0, // Set default to 0 instead of empty string
  });
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchTeam();
    }
  }, [id]);

  const fetchTeam = async () => {
    try {
      const data = await getTeamById(id);
      setInitialValues({
        name: data.name,
        nameAr: data.nameAr || '',
        role: data.role,
        roleAr: data.roleAr || '',
        bio: data.bio,
        bioAr: data.bioAr || '',
        imageSrc: data.imageSrc,
        order: parseInt(data.order) || 0, // Ensure it's a number
      });
    } catch (error) {
      console.error('Error fetching team member:', error);
    }
  };

  const TeamSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    role: Yup.string().required('Role is required'),
    bio: Yup.string().required('Bio is required'),
    order: Yup.number()
      .required('Order is required')
      .integer('Order must be an integer')
      .min(0, 'Order must be a non-negative number')
      .transform((value) => (isNaN(value) ? undefined : value)), // Handle empty string to undefined
    // Arabic fields are optional
    nameAr: Yup.string(),
    roleAr: Yup.string(),
    bioAr: Yup.string(),
  });

  const handleSubmit = async (values) => {
    try {
      const teamData = {
        name: values.name,
        nameAr: values.nameAr,
        role: values.role,
        roleAr: values.roleAr,
        bio: values.bio,
        bioAr: values.bioAr,
        imageSrc: values.imageSrc,
        order: parseInt(values.order) || 0, // Ensure it's a number when submitting
      };

      if (id) {
        await updateTeam(id, teamData);
      } else {
        await createTeam(teamData);
      }
      navigate('/team');
    } catch (error) {
      console.error('Error saving team member:', error);
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="md">
        <Box mt={4}>
          <Typography variant="h4" gutterBottom>
            {id ? 'Edit Team Member' : 'Create Team Member'}
          </Typography>
          
          <Formik
            initialValues={initialValues}
            enableReinitialize={true} // Ensure this is true
            validationSchema={TeamSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, setFieldValue }) => (
              <Form>
                {/* Order Field */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2, color: 'text.secondary' }}>
                  Display Order
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <TextField
                  label="Order"
                  name="order"
                  value={values.order}
                  onChange={(e) => {
                    // Custom handler to ensure numeric value
                    const value = e.target.value;
                    setFieldValue('order', value === '' ? '' : parseInt(value) || 0);
                  }}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  type="number"
                  inputProps={{ min: 0 }}
                  error={touched.order && Boolean(errors.order)}
                  helperText={touched.order && errors.order}
                />

                {/* English Fields Section */}
                <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: 'primary.main' }}>
                  English Content
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <TextField
                  label="Name (English)"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />

                <TextField
                  label="Role (English)"
                  name="role"
                  value={values.role}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.role && Boolean(errors.role)}
                  helperText={touched.role && errors.role}
                />
                
                <Box mt={2} mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Bio (English) *
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={values.bio}
                    onChange={(value) => setFieldValue('bio', value)}
                    modules={{
                      toolbar: [
                        [{ header: '1' }, { header: '2' }, { font: [] }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                        [{ align: [] }],
                        [{ color: [] }, { background: [] }],
                        ['link', 'image'],
                        ['clean'],
                      ],
                    }}
                    placeholder="Enter team member biography..."
                  />
                  {touched.bio && errors.bio && (
                    <Typography color="error" sx={{ mt: 1 }}>{errors.bio}</Typography>
                  )}
                </Box>

                {/* Arabic Fields Section */}
                <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: 'secondary.main' }}>
                  Arabic Content (اختياري)
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <TextField
                  label="Name (Arabic) - الاسم"
                  name="nameAr"
                  value={values.nameAr}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  inputProps={{ dir: 'rtl' }}
                  error={touched.nameAr && Boolean(errors.nameAr)}
                  helperText={touched.nameAr && errors.nameAr}
                />

                <TextField
                  label="Role (Arabic) - المنصب"
                  name="roleAr"
                  value={values.roleAr}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  inputProps={{ dir: 'rtl' }}
                  error={touched.roleAr && Boolean(errors.roleAr)}
                  helperText={touched.roleAr && errors.roleAr}
                />
                
                <Box mt={2} mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Bio (Arabic) - السيرة الذاتية
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={values.bioAr}
                    onChange={(value) => setFieldValue('bioAr', value)}
                    modules={{
                      toolbar: [
                        [{ header: '1' }, { header: '2' }, { font: [] }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                        [{ align: [] }],
                        [{ color: [] }, { background: [] }],
                        ['link', 'image'],
                        ['clean'],
                      ],
                    }}
                    placeholder="أدخل السيرة الذاتية لعضو الفريق..."
                  />
                  {touched.bioAr && errors.bioAr && (
                    <Typography color="error" sx={{ mt: 1 }}>{errors.bioAr}</Typography>
                  )}
                </Box>

                {/* Additional Information Section */}
                <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, color: 'text.secondary' }}>
                  Additional Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <MediaUploadField
                  label="Team Member Image"
                  value={values.imageSrc}
                  onChange={(nextValue) => setFieldValue('imageSrc', nextValue)}
                  kind="image"
                  scope="team"
                  slug={values.name}
                  error={errors.imageSrc}
                  touched={touched.imageSrc}
                />

                <Box mt={4}>
                  <Button type="submit" variant="contained" color="primary" size="large" fullWidth>
                    {id ? 'Update Team Member' : 'Create Team Member'}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Container>
    </DashboardLayout>
  );
}

export default TeamForm;
